import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { Location } from '@angular/common';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  FormControl,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
// RdsButtonModule / RdsCheckboxModule / RdsFormFieldModule are deprecated in
// RDS 4.21+ but still used throughout inexp (S1874 fires ~650× project-wide).
// Migration to standalone components is a repo-wide effort, not this branch's.
import {
  RdsAlertComponent,
  RdsAutocompleteComponent,
  RdsAutocompleteHighlightDirective,
  RdsAutocompleteOptionDirective,
  RdsBadgeComponent,
  RdsButtonModule, // NOSONAR: S1874 — repo-wide RDS module migration pending
  RdsCheckboxModule, // NOSONAR: S1874 — repo-wide RDS module migration pending
  RdsFormFieldModule, // NOSONAR: S1874 — repo-wide RDS module migration pending
  RdsIconComponent,
  RdsMultiAutocompleteComponent,
  RdsMultiSelectListItemComponent,
  RdsProgressSpinnerComponent,
  RdsRadioButtonComponent,
  RdsRadioGroupDirective,
  RdsSingleSelectListItemComponent,
  RdsTooltipDirective,
} from '@rds/angular-components';
import {
  catchError,
  combineLatest,
  EMPTY,
  finalize,
  forkJoin,
  from,
  map,
  Observable,
  of,
  startWith,
  Subject,
  switchMap,
} from 'rxjs';
import { FeatureFlagDirective } from '../../../directives/feature-flag.directive';
import { CatalogAttachmentsComponent } from '../catalog-attachments/catalog-attachments.component';
import {
  CatalogClientScript,
  CatalogItem,
  CatalogMetadata,
  CatalogMetadataItem,
  CatalogVariable,
  ReferenceOption,
  RenderSection,
  RenderVariable,
} from '../../../models/catalog-form';
import { AuthService } from '../../../services/auth.service';
import { CatalogFormService } from '../../../services/catalog-form.service';
import { GlideAjaxBridgeService } from '../../../services/glide-ajax-bridge.service';
import { MulesoftService } from '../../../services/mulesoft.service';
import { MulesoftOffering } from '../../../models/mulesoft-offering';
import { MercuryFormService } from '../../../services/mercury-form.service';
import {
  FormConfigDetails,
  FormSubmissionResponse,
  SubmitFormPayload,
  UserDetails,
} from '../../../models/mercury-form';
import { FulfillmentUtils } from '../../../util/fulfillment-utils';
import { toSubmitAttachment } from '../../../util/submit-attachment';
import { extractTicketResponseMessage } from '../../../util/ticket-error';
import { GFormChange, GFormMock } from '../../../util/g-form-mock';
import {
  UIPolicyDelta,
  UIPolicyEvaluator,
} from '../../../util/ui-policy-evaluator';
import {
  CATALOG_ATTACHMENT_TEXTS,
  CATALOG_FORM_TEXTS,
} from '../../../constants/catalog-form.constants';
import {
  cleanRichTextHtml,
  groupIntoSections,
  mergeVariables,
  normaliseDefault,
  resolveOnChangeTarget,
} from './catalog-form.utils';

// See INEXP-2496. Gated in Unleash; in local dev the flag defaults to true
// because FeatureFlagService.createFlagSignal returns true until Unleash is
// ready (see feature-flag.service.ts).
export const SNOW_RENDERER_FLAG = 'focus_inexp_2496_snow_renderer';

// INEXP-2497: where Back lands when there is no in-app history to return to
// (deep link, refresh, new tab).
export const CATALOG_FORM_BACK_FALLBACK_URL = '/webexp';

// RDS autocomplete owns the input element and handles keystroke debouncing
// internally via its `debounce` input — matches the operator chain in
// shared/search-input.component.ts (300ms + distinctUntilChanged + switchMap).
export const REFERENCE_TYPEAHEAD_DEBOUNCE_MS = 300;
export const REFERENCE_TYPEAHEAD_MIN_CHARS = 2;
export const REFERENCE_TYPEAHEAD_LIMIT = 10;

// How long Submit waits for in-flight GlideAjax answers before treating them
// as unavailable (the proxy itself allows up to 25 s).
export const SUBMIT_GLIDE_AJAX_WAIT_MS = 10_000;

type CatalogFormState = 'loading' | 'error' | 'ready' | 'submitted';

type ReferenceOptionsFn = (query: string) => Observable<ReferenceOption[]>;

@Component({
  selector: 'inexp-catalog-form',
  imports: [
    ReactiveFormsModule,
    FeatureFlagDirective,
    CatalogAttachmentsComponent,
    RdsAlertComponent,
    RdsAutocompleteComponent,
    RdsAutocompleteHighlightDirective,
    RdsAutocompleteOptionDirective,
    RdsBadgeComponent,
    RdsButtonModule, // NOSONAR: S1874 — see import comment
    RdsCheckboxModule, // NOSONAR: S1874 — see import comment
    RdsFormFieldModule, // NOSONAR: S1874 — see import comment
    RdsIconComponent,
    RdsMultiAutocompleteComponent,
    RdsMultiSelectListItemComponent,
    RdsProgressSpinnerComponent,
    RdsRadioButtonComponent,
    RdsRadioGroupDirective,
    RdsSingleSelectListItemComponent,
    RdsTooltipDirective,
  ],
  templateUrl: './catalog-form.component.html',
  styleUrl: './catalog-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'inexp-catalog-form',
  },
})
export class CatalogFormComponent {
  readonly sysId = input<string>();

  readonly state = signal<CatalogFormState>('loading');
  readonly errorMessage = signal<string | null>(null);
  // Human-readable reasons SN gave for `available: false` on the availability
  // endpoint. Rendered as a bullet list under the error card so the user
  // knows what to do (join a group, request access, etc.). Empty for
  // generic errors that don't come from the entitlement check.
  readonly notEntitledReasons = signal<string[]>([]);
  readonly item = signal<CatalogItem | null>(null);
  readonly variables = signal<RenderVariable[]>([]);
  readonly ritmNumber = signal<string | null>(null);
  // INEXP-2497: Back sits above the loading overlay so it stays usable while
  // the form loads, but leaving mid-submit would lose the ticket number.
  readonly isSubmitting = signal(false);
  // Full human-readable message from the fulfillment endpoint's success
  // response (e.g. "Offering Fulfillment Request Submitted Successfully with
  // SNOW TicketNumber: REQ6442558"). Rendered verbatim in the submitted
  // state card so users see the real backend confirmation instead of a
  // dev-preview placeholder.
  readonly submitMessage = signal<string | null>(null);

  // How many client scripts / UI policies the metadata carried for this
  // item. Observable in tests and dev tools; the scripts themselves run in
  // `_wireSdkRuntime`.
  readonly clientScriptCount = signal(0);
  readonly uiPolicyCount = signal(0);

  readonly title = computed(() => this.item()?.name ?? 'ServiceNow request');
  // r2f 1.1.3 metadata `item` block (mandatoryAttachment, noOrder, …).
  readonly metadataItem = signal<CatalogMetadataItem | null>(null);
  readonly shortDescription = computed(
    () => this.item()?.shortDescription || this.metadataItem()?.shortDescription || ''
  );
  // r2f 1.1.3: rich HTML — bound with [innerHTML], which sanitises.
  readonly itemDescription = computed(() => cleanRichTextHtml(this.item()?.description || ''));
  // r2f 1.1.3 §3: the item cannot be ordered from the standard catalog.
  readonly noOrder = computed(() => this.metadataItem()?.noOrder === true);

  // The /formConfigDetails entry whose `formId` is this catalog sysId, and the
  // offering it belongs to. They feed the submit payload's
  // `formConfigDetails` and the right panel. Null until resolved, or when the
  // lookup fails; the panel then shows only what the catalog item provides.
  readonly formConfig = signal<FormConfigDetails | null>(null);
  readonly offering = signal<MulesoftOffering | null>(null);

  // Right-panel offering context, from the offering record (same fields the
  // offering page hero and details use).
  readonly offeringName = computed(
    () => this.offering()?.displayName || this.offering()?.offeringName || this.title(),
  );
  readonly approvalBadgeLabel = computed(() => this.offering()?.approvalStatus || null);
  readonly licenseCostResponsibility = computed(
    () => this.offering()?.licenseCostResponsibility || null,
  );
  readonly offeringDescription = computed(() => this.offering()?.description || null);

  // Right-panel Service Book link. The offering record has no Service Book
  // URL yet, so this stays null and the link is hidden — a placeholder '#'
  // would render a link that reloads the current page.
  readonly serviceBookUrl = signal<string | null>(null);
  readonly CATALOG_FORM_TEXTS = CATALOG_FORM_TEXTS;
  readonly submitLabel = computed(
    () => this.item()?.submitButtonLabel ?? 'Submit'
  );
  readonly visibleVariables = computed(() =>
    this.variables().filter((v) => v.visible)
  );

  // Section grouping per guide §5.4 — container_start opens a section, its
  // matching container_end closes it, everything in between becomes that
  // section's fields. Fields outside any container land in an untitled section.
  readonly sections = computed<RenderSection[]>(() =>
    groupIntoSections(this.visibleVariables())
  );

  // Encoded SN query strings resolved once from `javascript:` refQualifiers on
  // the current form. Non-`javascript:` refQualifiers pass through untouched
  // and are read straight off the RenderVariable. Populated in `_toReady`.
  readonly resolvedRefQuery = signal<Record<string, string>>({});

  // First page of matching records the server pre-evaluated alongside each
  // `javascript:` refQualifier. Seeds the typeahead on first open so the
  // dropdown appears without a second `referenceLookup` round-trip (Mulesoft
  // doc §2.4 explicitly recommends this). Cleared alongside resolvedRefQuery.
  readonly prefetchedRefRecords = signal<Record<string, ReferenceOption[]>>({});

  // Per-reference-field secondary-column ordering derived from the metadata
  // `refAcColumns` attribute (guide §5.6). Populated in
  // `_buildReferenceSearchFactories` and preserved for consumers that render
  // multi-column dropdowns.
  readonly refColumns = signal<Record<string, string[]>>({});

  // Debounce for the reference typeahead. `RdsAutocompleteComponent` owns the
  // input element and handles debouncing / distinctUntilChanged / switchMap
  // internally when `options` is a search function — matches the operator
  // chain in `shared/search-input.component.ts` (300ms + distinct + switchMap).
  readonly referenceDebounceMs = REFERENCE_TYPEAHEAD_DEBOUNCE_MS;
  readonly referenceMinChars = REFERENCE_TYPEAHEAD_MIN_CHARS;

  readonly SNOW_RENDERER_FLAG = SNOW_RENDERER_FLAG;

  // INEXP-2383: the item response drives the attachment section, as on the
  // RoSE catalog item widget — hidden when `noAttachments` is true, required
  // (asterisk + submit blocked) when `mandatoryAttachment` is true.
  readonly attachmentsAllowed = computed(() => {
    const noAttachments = this.item()?.noAttachments;
    return noAttachments !== true && noAttachments !== 'true';
  });
  // r2f 1.1.3: metadata `item.mandatoryAttachment` is the boolean contract;
  // the item flag (string or boolean on older payloads) still counts.
  readonly attachmentRequired = computed(() => {
    const mandatory = this.item()?.mandatoryAttachment;
    const required =
      mandatory === true || mandatory === 'true' || this.metadataItem()?.mandatoryAttachment === true;
    return this.attachmentsAllowed() && required;
  });
  // Files the user picked that passed the size check.
  readonly attachments = signal<File[]>([]);
  // Shown in the attachment section after a failed submit: a required file is
  // missing, or a picked file could not be read. Cleared once a file is added.
  readonly attachmentError = signal<string | null>(null);

  // A client script's GlideAjax call got a 403 from the proxy (Script Include
  // not yet approved by a SNOW admin). Shown as a notice; the form stays usable.
  readonly dynamicValuesUnavailable = signal(false);

  // Client-script messages: `g_form.showFieldMsg` per field and
  // `g_form.addErrorMessage` for the whole form. Cleared by hideFieldMsg /
  // clearMessages, and on every form load.
  readonly fieldMessages = signal<Record<string, { text: string; type: 'info' | 'error' }>>({});
  readonly formErrors = signal<string[]>([]);

  // Reference fields whose options cannot be offered: an advanced / dynamic
  // qualifier failed closed ('empty') or the evaluation errored ('error').
  // r2f 1.1.3 §6 — never fall back to an unfiltered lookup.
  readonly refUnavailable = signal<Record<string, 'empty' | 'error'>>({});

  // Masked fields currently shown in clear text.
  readonly revealedMasked = signal<Record<string, boolean>>({});

  // An onSubmit client script returned false. Shown by the submit button so
  // the click does not silently do nothing.
  readonly submitBlocked = signal(false);

  // Heading of the currently-visible state card. Present in error / submitted /
  // ready blocks with tabindex="-1" so it can receive programmatic focus (see
  // the state-transition effect in the constructor). Undefined during the
  // initial 'loading' state.
  private readonly _stateHeading = viewChild<ElementRef<HTMLElement>>('stateHeading');
  private readonly _attachmentsSection = viewChild(CatalogAttachmentsComponent);

  private readonly _catalogFormService = inject(CatalogFormService);
  // Identity for the submit payload's `userDetails` (`_currentUserDetails`).
  private readonly _auth = inject(AuthService);
  // Reuses the mercury-form `/offeringFulfillmentRequest` endpoint so
  // catalog-form submits land in the same fulfillment queue with the same
  // payload envelope (see `_buildSubmitPayload`).
  private readonly _mercuryFormService = inject(MercuryFormService);
  private readonly _mulesoftService = inject(MulesoftService);
  private readonly _fb = inject(NonNullableFormBuilder);
  // Consumed by `_resolveJavascriptRefQualifiers`, which subscribes outside
  // the constructor's injection context and passes this to takeUntilDestroyed.
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _injector = inject(Injector);
  // Bridge used by GFormMock's GlideAjax proxy — see util/g-form-mock.ts.
  private readonly _glideBridge = inject(GlideAjaxBridgeService);
  private readonly _location = inject(Location);
  private readonly _router = inject(Router);
  private readonly _retry$ = new Subject<void>();


  // Tracks the prior state so the focus-move effect can distinguish an
  // initial land ('loading' → 'ready') from a user-driven transition
  // (submit → 'submitted', load failure → 'error'). We only move focus on
  // the latter to avoid a jarring focus shift on first paint.
  private _prevState: CatalogFormState = 'loading';

  // One search factory per reference field. Built in `_toReady` so the
  // function identity is stable across change-detection cycles (RDS's
  // `options` input is not re-subscribed unless the reference changes).
  private _referenceSearch: Record<string, ReferenceOptionsFn> = {};

  // SDK runtime. Non-null once the form has resolved.
  private _evaluator: UIPolicyEvaluator | null = null;
  private _gForm: GFormMock | null = null;
  // `watched` is the form control the script listens to; scripts whose
  // target cannot be resolved are dropped (see `_resolveWatchedField`).
  private _onChangeScripts: {
    watched: string;
    script: string;
  }[] = [];
  // Plain-value snapshot from the previous pass, diffed to find which field
  // actually changed so only its onChange scripts fire (SN semantics).
  private _lastValues: Record<string, unknown> = {};
  private _onSubmitScripts: string[] = [];

  form = this._fb.group<Record<string, FormControl<unknown>>>({});

  constructor() {
    combineLatest([
      toObservable(this.sysId),
      this._retry$.pipe(startWith(void 0)),
    ])
      .pipe(
        switchMap(([sysId]) => {
          if (!sysId) {
            this._toReady(null, null);
            return of(null);
          }
          this._toLoading();
          // Mulesoft doc STEP 0 — availability first. If the user is not
          // entitled, short-circuit to the not-entitled state and never
          // fetch item/metadata. If the availability call itself fails
          // (404 for a bad sysId, 5xx from the proxy), route to the generic
          // error state — better to tell the user something went wrong than
          // to load a form they may not be entitled to submit.
          return this._catalogFormService.checkAvailability(sysId).pipe(
            switchMap((availability) => {
              if (availability?.available === false) {
                this._toNotEntitled(availability.reasons ?? []);
                return of(null);
              }
              return forkJoin({
                item: this._catalogFormService.loadCatalogItem(sysId),
                metadata: this._catalogFormService.loadCatalogMetadata(
                  sysId,
                  this._auth.email() ?? undefined,
                ),
              });
            }),
            catchError((err: unknown) => {
              this._toError(err);
              return of(null);
            }),
          );
        }),
        // No `_destroyRef` arg needed — called from the constructor's
        // injection context, so takeUntilDestroyed picks it up implicitly.
        takeUntilDestroyed(),
      )
      .subscribe((result) => {
        if (result) {
          this._toReady(result.item, result.metadata);
        }
      });

    // Offering context. Independent of the form load: a failure here never
    // blocks the form, it only leaves the panel and payload on catalog data.
    toObservable(this.sysId)
      .pipe(
        switchMap((sysId) => {
          this.formConfig.set(null);
          this.offering.set(null);
          return sysId ? this._loadFormConfig(sysId) : of(null);
        }),
        switchMap((config) => {
          this.formConfig.set(config);
          const offeringId = config?.offeringId;
          if (!offeringId) return of(null);
          return this._mulesoftService
            .getOffering(String(offeringId), 'published', { includeKnowledge: false })
            .pipe(catchError(() => of(null)));
        }),
        takeUntilDestroyed(),
      )
      .subscribe((offering) => this.offering.set(offering ?? null));

    // Announce state changes to assistive tech by moving focus to the state
    // heading. AT users need a positive cue when async content resolves, so
    // the initial 'loading' → 'ready' transition also focuses (per a11y round
    // 2 — the sr-only h1 only renders once item() resolves, so this is the
    // only signal that the form is usable). Skip when next is 'loading'
    // (no heading to focus yet) or when the state didn't actually change.
    effect(() => {
      const next = this.state();
      const prev = this._prevState;
      this._prevState = next;
      if (next === 'loading' || prev === next) return;
      afterNextRender(
        () => this._stateHeading()?.nativeElement.focus(),
        { injector: this._injector },
      );
    });
  }

  retry(): void {
    this._retry$.next();
  }

  onAttachmentsChange(files: File[]): void {
    this.attachments.set(files);
    if (files.length > 0) this.attachmentError.set(null);
  }

  submit(): void {
    // Ignore repeat calls while a submit (or its onSubmit checks) is running;
    // the button is disabled then, but Enter in a field still submits.
    if (this.isSubmitting()) return;
    this.submitBlocked.set(false);
    const attachmentMissing =
      this.attachmentRequired() && this.attachments().length === 0;
    this.attachmentError.set(
      attachmentMissing ? CATALOG_ATTACHMENT_TEXTS.REQUIRED_ERROR : null,
    );
    if (this.form.invalid || attachmentMissing) {
      this.form.markAllAsTouched();
      // Only the attachment is missing: take keyboard users to it (it sits
      // at the end of a long form). The picker is described by the error.
      if (!this.form.invalid) this._attachmentsSection()?.focusPicker();
      return;
    }
    // Fast path — no onSubmit scripts.
    // Kept synchronous so the existing test contract holds and the submit is
    // indistinguishable from the pre-SDK flow.
    if (!this._gForm || this._onSubmitScripts.length === 0) {
      this._doSubmit();
      return;
    }
    // Guide §7: onSubmit scripts may return false to abort.
    void this._submitWithSdkGuard();
  }

  private async _submitWithSdkGuard(): Promise<void> {
    // The onSubmit checks may wait on GlideAjax answers; show the click
    // registered instead of a Submit button that seems to do nothing.
    this.isSubmitting.set(true);
    const allowed = await this._runOnSubmitScripts();
    if (!allowed) {
      this.isSubmitting.set(false);
      return;
    }
    this._doSubmit();
  }

  goBack(): void {
    // Get Started opens the form in the same tab, so Back returns to the
    // offering page. Angular stamps navigationId on history.state; 1 means
    // this page was the first route loaded, so there is nothing to go back to.
    const state = this._location.getState() as { navigationId?: number } | null;
    if ((state?.navigationId ?? 1) > 1) {
      this._location.back();
      return;
    }
    void this._router.navigateByUrl(CATALOG_FORM_BACK_FALLBACK_URL);
  }

  private _doSubmit(): void {
    const payload = this._buildSubmitPayload();
    const files = this.attachments();
    this.state.set('loading');
    this.isSubmitting.set(true);
    // Attachments travel inline in the same request (base64), so they are
    // read first. No files: post straight away, as before.
    const payload$ =
      files.length === 0
        ? of(payload)
        : from(Promise.all(files.map(toSubmitAttachment))).pipe(
            map((attachments) => ({
              requestDetails: { ...payload.requestDetails, attachments },
            })),
            catchError(() => {
              this._onAttachmentReadError();
              return EMPTY;
            }),
          );
    payload$
      .pipe(
        switchMap((body) => this._mercuryFormService.submitForm(body)),
        finalize(() => this.isSubmitting.set(false)),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe({
        next: (res: FormSubmissionResponse) => {
          const message =
            typeof res?.message === 'string' ? res.message : null;
          this.submitMessage.set(message);
          // Prefer the ticket number the backend echoed in its message (e.g.
          // "…SNOW TicketNumber: REQ6442558"); fall back to the legacy
          // requestId field. No ticket means none is shown — never invent one.
          this.ritmNumber.set(
            this._extractTicketNumber(message) ?? res?.requestId ?? null,
          );
          this.state.set('submitted');
        },
        error: (err: unknown) => this._toSubmitError(err),
      });
  }

  // Submit rejected. Show ServiceNow's reason from the ticket envelope
  // (`message.TicketResponseMessage`) rather than Angular's technical
  // "Http failure response for …" text.
  private _toSubmitError(err: unknown): void {
    this.errorMessage.set(
      extractTicketResponseMessage(err) ?? CATALOG_FORM_TEXTS.SUBMIT_ERROR_FALLBACK,
    );
    this.state.set('error');
  }

  // A picked file could not be read (moved, deleted, permission lost). Nothing
  // was sent. Back to the form: the attachment list is rebuilt empty while
  // submitting, so the user is asked to add the files again.
  private _onAttachmentReadError(): void {
    this.attachments.set([]);
    this.attachmentError.set(CATALOG_ATTACHMENT_TEXTS.READ_ERROR);
    this.isSubmitting.set(false);
    this.state.set('ready');
  }

  private _extractTicketNumber(message: string | null): string | null {
    if (!message) return null;
    // SNOW request numbers follow REQ/RITM/INC/CHG prefixes + 7-10 digits.
    const match = /\b(REQ|RITM|INC|CHG)\d{6,10}\b/.exec(message);
    return match ? match[0] : null;
  }

  private _currentUserDetails(): UserDetails {
    const cid = this._auth.cId() ?? '';
    const fullName = [this._auth.first() ?? '', this._auth.last() ?? '']
      .filter((s) => s.trim().length > 0)
      .join(' ');
    return {
      userId: cid,
      userName: fullName || cid,
      email: this._auth.email() ?? '',
    };
  }

  // Navigation state from the offering page (kept out of the URL); picks the
  // right /formConfigDetails entry when several offerings share this catalog
  // item. Undefined on a shared link.
  private _offeringId(): string | undefined {
    return (this._location.getState() as { offeringId?: string } | null)?.offeringId;
  }

  // Same lookup as mercury-form: the offering page caches /formConfigDetails
  // before Get Started; a deep link or refresh fetches it and picks our entry.
  private _loadFormConfig(sysId: string): Observable<FormConfigDetails | null> {
    const cached = this._mercuryFormService.getFormConfigByFormId(sysId, this._offeringId());
    if (cached) return of(cached);
    return this._mercuryFormService.getFormConfigDetails('').pipe(
      map((res) => {
        const list = res?.offeringFormDetails ?? [];
        // Cache it, as the offering page does, so a reload of this form
        // (retry, another sysId) does not refetch the full list.
        this._mercuryFormService.setFormConfigs(list);
        return FulfillmentUtils.findFormConfig(list, sysId, this._offeringId()) ?? null;
      }),
      catchError(() => of(null)),
    );
  }

  // The real /formConfigDetails entry when it resolved; otherwise a minimal
  // config derived from the catalog item so submit still works.
  private _buildFormConfig(): FormConfigDetails {
    const config = this.formConfig();
    // r2f-proc resolves the ServiceNow catalog item from `offeringName`
    // (mapped to offeringDetails.offeringName / displayName): with the
    // offering's name it rejects the submit with 400 "Catalog Item: 'Galileo
    // Platform' does not exist." Keep the catalog item name there; every
    // other field comes from the real /formConfigDetails entry.
    if (config) return { ...config, offeringName: this.item()?.name ?? config.offeringName };
    const sysId = this.sysId() ?? '';
    const name = this.item()?.name ?? '';
    return {
      formId: sysId,
      offeringId: sysId,
      offeringName: name,
      description: this.item()?.shortDescription ?? '',
      fulfillmentType: 'servicenow',
      fulfillmentMode: 'servicenow',
      fulfillmentChannel: 'servicenow',
      fulfillmentTitle: name,
    };
  }

  // Mirrors mercury-form's `FulfillmentUtils.buildSubmitPayload` — user
  // identity from AuthService, minimal `formConfigDetails` seeded from the
  // catalog item (SNOW does not surface a Mulesoft `formId`, so we use the
  // catalog sysId as a stable per-item key), and every visible form control
  // value spread into `fulfillmentParameters` so the fulfillment queue sees
  // the same shape as an iframe-driven mercury submit.
  private _buildSubmitPayload(): SubmitFormPayload {
    return FulfillmentUtils.buildSubmitPayload(
      this._currentUserDetails(),
      this._collectFormValues(),
      this._buildFormConfig(),
    );
  }

  // Reactive form → flat key/value map. `getRawValue()` includes disabled
  // controls (readonly fields with prefilled values must still submit).
  // Reference-typed controls hold `ReferenceOption` objects — flatten to
  // `sys_id` for the primary key and stash the human label under
  // `<name>_displayValue` so the fulfillment queue keeps enough context to
  // render the request back to a human. `glide_list` (reference_multi)
  // controls hold `ReferenceOption[]`; SN convention is a comma-joined
  // sys_id string on submit + a comma-joined display-value sibling.
  private _collectFormValues(): Record<string, unknown> {
    const raw = this.form.getRawValue() as Record<string, unknown>;
    const flat: Record<string, unknown> = {};
    for (const [name, value] of Object.entries(raw)) {
      if (this._isReferenceOptionArray(value)) {
        flat[name] = value.map((r) => r.sys_id).join(',');
        flat[`${name}_displayValue`] = value
          .map((r) => r.display_value)
          .join(',');
      } else if (this._isReferenceOption(value)) {
        flat[name] = value.sys_id;
        flat[`${name}_displayValue`] = value.display_value;
      } else {
        flat[name] = value;
      }
    }
    return flat;
  }

  private _isReferenceOption(
    v: unknown,
  ): v is { sys_id: string; display_value: string } {
    return (
      v !== null &&
      typeof v === 'object' &&
      'sys_id' in v &&
      'display_value' in v
    );
  }

  private _isReferenceOptionArray(
    v: unknown,
  ): v is { sys_id: string; display_value: string }[] {
    return (
      Array.isArray(v) && v.length > 0 && v.every((r) => this._isReferenceOption(r))
    );
  }

  reset(): void {
    this.ritmNumber.set(null);
    this.submitMessage.set(null);
    this._rebuildForm(this.variables());
    this.state.set('ready');
  }

  // Template binding. Returns the stable search factory for a reference field
  // built in `_toReady`. Falls back to a no-op factory for unknown names so
  // change detection during transient states doesn't throw.
  referenceOptionsFor(name: string): ReferenceOptionsFn {
    return this._referenceSearch[name] ?? (() => of([]));
  }

  // Template binding for the typeahead option's secondary line — the
  // `ref_ac_columns` extras SN shows beside the display value (e.g.
  // `user_name · email` on sys_user, manufacturer / model on devices).
  // Ordered as the field's `ref_ac_columns` lists them; columns the record
  // didn't echo, or echoed empty, are skipped.
  referenceOptionDetail(name: string, option: ReferenceOption): string {
    const columns = option.columns ?? {};
    const order = this.refColumns()[name] ?? Object.keys(columns);
    return order
      .map((key) => columns[key]?.trim() ?? '')
      .filter((value) => value.length > 0)
      .join(' · ');
  }

  private _toLoading(): void {
    this.errorMessage.set(null);
    this.notEntitledReasons.set([]);
    this.ritmNumber.set(null);
    this.submitMessage.set(null);
    this.attachments.set([]);
    this.attachmentError.set(null);
    this.dynamicValuesUnavailable.set(false);
    this.submitBlocked.set(false);
    this.item.set(null);
    this.variables.set([]);
    this.clientScriptCount.set(0);
    this.uiPolicyCount.set(0);
    this.metadataItem.set(null);
    this.fieldMessages.set({});
    this.formErrors.set([]);
    this.refUnavailable.set({});
    this.revealedMasked.set({});
    this.resolvedRefQuery.set({});
    this.prefetchedRefRecords.set({});
    this.refColumns.set({});
    this._referenceSearch = {};
    this._evaluator = null;
    this._gForm = null;
    this._onChangeScripts = [];
    this._lastValues = {};
    this._onSubmitScripts = [];
    this.form = this._fb.group<Record<string, FormControl<unknown>>>({});
    this.state.set('loading');
  }

  // Availability STEP 0 returned `available: false`. Surface the reasons the
  // proxy provided (or a generic fallback if the array was empty) so the
  // user knows why they can't submit — this is separate from _toError, which
  // handles transport / server failures.
  private _toNotEntitled(reasons: string[]): void {
    const fallback = ['You are not entitled to submit this catalog item.'];
    this.notEntitledReasons.set(reasons.length > 0 ? reasons : fallback);
    this.errorMessage.set(null);
    this.state.set('error');
  }

  private _toError(err: unknown): void {
    const message =
      err && typeof err === 'object' && 'message' in err
        ? String(err.message)
        : 'We could not load this request form.';
    this.errorMessage.set(message);
    this.state.set('error');
  }

  private _toReady(
    item: CatalogItem | null,
    metadata: CatalogMetadata | null
  ): void {
    if (!item) {
      this.item.set(null);
      this.variables.set([]);
      this._referenceSearch = {};
      this.resolvedRefQuery.set({});
      this.prefetchedRefRecords.set({});
      this.refColumns.set({});
      this.state.set('ready');
      return;
    }
    const merged = mergeVariables(item.variables, metadata);
    this.item.set(item);
    this.metadataItem.set(metadata?.item ?? null);
    this.variables.set(merged);
    this.clientScriptCount.set(metadata?.clientScripts?.length ?? 0);
    this.uiPolicyCount.set(metadata?.uiPolicies?.length ?? 0);
    this._rebuildForm(merged);
    this._buildReferenceSearchFactories(merged);
    this._resolveJavascriptRefQualifiers(merged);
    this._wireSdkRuntime(item, metadata);
    this.state.set('ready');
  }

  // Runtime SDK wiring, run on every form load. Builds the UIPolicyEvaluator + GFormMock for this form, runs any onLoad scripts,
  // applies drained state changes, and subscribes to form.valueChanges so
  // conditional policies + onChange scripts fire on every user input.
  private _wireSdkRuntime(
    item: CatalogItem,
    metadata: CatalogMetadata | null,
  ): void {
    const policies = metadata?.uiPolicies ?? [];
    this._evaluator = new UIPolicyEvaluator(policies);

    this._gForm = new GFormMock({
      itemSysId: this.sysId() ?? '',
      currentValues: this._collectPlainValues(),
      bridge: this._glideBridge,
      // Only Script Includes Mulesoft auto-discovered for this item may be
      // proxied through /catalog/executeGlideAjax.
      allowedScriptIncludes: (metadata?.discoveredSis ?? []).filter(
        (si): si is string => typeof si === 'string',
      ),
      onScriptIncludeUnavailable: () => this.dynamicValuesUnavailable.set(true),
      displayValueOf: (name) => this._displayValueOf(name),
    });

    const scripts = metadata?.clientScripts ?? [];
    // Cache the onChange / onSubmit scripts so per-change / per-submit
    // dispatch does not re-walk the whole metadata array.
    this._onChangeScripts = scripts
      .filter((s) => s.type === 'onChange')
      .map((s) => ({ watched: this._resolveWatchedField(s, metadata), script: s.script }))
      .filter((s): s is { watched: string; script: string } => s.watched !== null);
    this._onSubmitScripts = scripts
      .filter((s) => s.type === 'onSubmit')
      .map((s) => s.script);

    void this._runInitialSdkPass(scripts.filter((s) => s.type === 'onLoad'));

    this._lastValues = this._collectPlainValues();
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        void this._runOnChangePass();
      });
  }

  private async _runInitialSdkPass(
    onLoadScripts: { script: string }[],
  ): Promise<void> {
    // Guide §13 rule 7: client scripts run first, UI policies overlay last.
    if (this._gForm) {
      for (const s of onLoadScripts) {
        await this._gForm.runScript(s.script, {
          type: 'onLoad',
          isLoading: true,
        });
      }
      // SN also fires every onChange script once on load with
      // `isLoading = true` (e.g. "Autopopulate Username, email, dept" fills
      // the requested-for details from the prefilled reference).
      const values = this._collectPlainValues();
      for (const s of this._onChangeScripts) {
        await this._gForm.runScript(s.script, {
          type: 'onChange',
          fieldName: s.watched,
          oldValue: '',
          newValue: values[s.watched] ?? '',
          isLoading: true,
        });
      }
      // GlideAjax callbacks run after the script returns; wait for them so
      // their setValue / setVisible land in this pass.
      await this._gForm.whenIdle();
      this._applyGFormChanges(this._gForm.drainChanges());
    }
    this._applyUIPolicyDelta();
    this._lastValues = this._collectPlainValues();
  }

  // Unresolvable scripts are skipped — running them on every change with an
  // empty `newValue` makes scripts like "Auto-fill BPO/PO" clear fields the
  // user already filled. `form.get` (not `form.contains`, which is false for
  // disabled controls) so read-only targets like requested_for still resolve.
  private _resolveWatchedField(
    script: CatalogClientScript,
    metadata: CatalogMetadata | null,
  ): string | null {
    const target = resolveOnChangeTarget(script, (n) => this.form.get(n) != null, metadata);
    if (target === null) {
      console.info(
        `[catalog-form] onChange script "${script.name ?? script.sysId}" skipped: target ${script.appliesToVariable ?? script.variableName ?? '?'} not resolvable to a field`,
      );
    }
    return target;
  }

  private async _runOnChangePass(): Promise<void> {
    if (!this._gForm) {
      this._applyUIPolicyDelta();
      return;
    }
    // Rehydrate the mock's mirror with the current form values before the
    // script runs — otherwise g_form.getValue() sees a stale snapshot.
    const values = this._collectPlainValues();
    for (const [name, value] of Object.entries(values)) {
      this._gForm.setValue(name, value);
    }
    // Drop the setValue changes we just pushed for state sync — they are
    // echoes of user input, not script side effects.
    this._gForm.drainChanges();

    // Watched === '' means "run on any change" (SN semantics). Named watches
    // fire only for their own field; without a specific field context here
    // (we listen to the whole valueChanges) we fire all onChange scripts on
    // every tick — sufficient for our current catalog items.
    const previous = this._lastValues;
    this._lastValues = values;
    for (const s of this._onChangeScripts) {
      if (values[s.watched] === previous[s.watched]) continue;
      await this._gForm.runScript(s.script, {
        type: 'onChange',
        fieldName: s.watched,
        oldValue: previous[s.watched] ?? '',
        newValue: values[s.watched] ?? '',
        isLoading: false,
      });
    }
    await this._gForm.whenIdle();
    this._applyGFormChanges(this._gForm.drainChanges());
    this._applyUIPolicyDelta();
    // Script-driven setValue calls do not emit; fold them into the snapshot
    // so they are not mistaken for user edits on the next pass.
    this._lastValues = this._collectPlainValues();
  }

  private _applyUIPolicyDelta(): void {
    if (!this._evaluator) return;
    const values = this._collectPlainValues();
    const delta = this._evaluator.evaluate(values);
    if (Object.keys(delta).length === 0) return;
    this._applyDelta(delta);
  }

  private _applyDelta(delta: UIPolicyDelta): void {
    this.variables.update((current) =>
      current.map((v) => {
        const patch = delta[v.name];
        if (!patch) return v;
        const next: RenderVariable = {
          ...v,
          visible: patch.visible ?? v.visible,
          mandatory: patch.mandatory ?? v.mandatory,
          readOnly: patch.readOnly ?? v.readOnly,
        };
        this._syncControlWith(next);
        return next;
      }),
    );
  }

  private _syncControlWith(v: RenderVariable): void {
    const control = this.form.get(v.name);
    if (!control) return;
    // Validator refresh: mirror `_rebuildForm`'s rule — only visible mandatory
    // fields carry a required validator (guide §13 rule 8).
    const requiredValidator =
      v.renderType === 'checkbox' ? Validators.requiredTrue : Validators.required;
    control.setValidators(
      v.mandatory && v.visible ? [requiredValidator] : [],
    );
    control.updateValueAndValidity({ emitEvent: false });
    if (v.readOnly && control.enabled) control.disable({ emitEvent: false });
    else if (!v.readOnly && control.disabled) control.enable({ emitEvent: false });
  }

  private _applyGFormChanges(changes: GFormChange[]): void {
    if (changes.length === 0) return;
    for (const ch of changes) {
      // Form-level messages (addErrorMessage / clearMessages) carry no field.
      if (ch.field == null) {
        this._applyMessageChange(ch);
        continue;
      }
      const control = this.form.get(ch.field);
      if (ch.op === 'setValue' && control) {
        control.setValue(ch.value ?? '', { emitEvent: false });
      } else if (ch.op === 'setVisible') {
        this._patchVariable(ch.field, { visible: Boolean(ch.value) });
      } else if (ch.op === 'setMandatory') {
        this._patchVariable(ch.field, { mandatory: Boolean(ch.value) });
      } else if (ch.op === 'setReadOnly') {
        this._patchVariable(ch.field, { readOnly: Boolean(ch.value) });
      } else {
        this._applyMessageChange(ch);
      }
      // clearOptions / addOption are drained but not yet applied.
    }
  }

  private _applyMessageChange(ch: GFormChange): void {
    const text = typeof ch.value === 'string' ? ch.value : '';
    if (ch.op === 'showFieldMsg' && ch.field) {
      const type = ch.label === 'error' ? 'error' : 'info';
      this.fieldMessages.update((m) => ({ ...m, [ch.field as string]: { text, type } }));
    } else if (ch.op === 'hideFieldMsg' && ch.field) {
      this.fieldMessages.update(({ [ch.field as string]: _removed, ...rest }) => rest);
    } else if (ch.op === 'addErrorMessage' && text) {
      this.formErrors.update((list) => (list.includes(text) ? list : [...list, text]));
    } else if (ch.op === 'clearMessages') {
      this.formErrors.set([]);
    }
  }

  // What the user sees for a field — backs g_form.getDisplayValue.
  private _displayValueOf(name: string): string | undefined {
    const value = this.form.get(name)?.value;
    if (this._isReferenceOption(value)) return value.display_value;
    if (this._isReferenceOptionArray(value)) return value.map((r) => r.display_value).join(', ');
    const variable = this.variables().find((v) => v.name === name);
    const choice = variable?.choices.find((c) => c.value === value);
    return choice?.label;
  }

  // Multiselect (checkbox list) — the control holds SN's comma-joined value.
  isMultiChecked(name: string, value: string): boolean {
    const raw = this.form.get(name)?.value;
    return typeof raw === 'string' && raw.split(',').includes(value);
  }

  toggleMulti(name: string, value: string, checked: boolean): void {
    const control = this.form.get(name);
    if (!control) return;
    const current = typeof control.value === 'string' && control.value ? control.value.split(',') : [];
    const next = checked ? [...new Set([...current, value])] : current.filter((v) => v !== value);
    control.setValue(next.join(','));
    control.markAsTouched();
  }

  toggleMasked(name: string): void {
    this.revealedMasked.update((m) => ({ ...m, [name]: !m[name] }));
  }

  // '' lets RDS use its own default "no results" text.
  referenceNoDataText(name: string): string {
    const state = this.refUnavailable()[name];
    if (state === 'error') return CATALOG_FORM_TEXTS.REFERENCE_LOOKUP_ERROR;
    if (state === 'empty') return CATALOG_FORM_TEXTS.REFERENCE_NO_OPTIONS;
    return '';
  }

  private _patchVariable(name: string, patch: Partial<RenderVariable>): void {
    this.variables.update((current) =>
      current.map((v) => {
        if (v.name !== name) return v;
        const next: RenderVariable = { ...v, ...patch };
        this._syncControlWith(next);
        return next;
      }),
    );
  }

  private _collectPlainValues(): Record<string, unknown> {
    const raw = this.form.getRawValue() as Record<string, unknown>;
    const flat: Record<string, unknown> = {};
    for (const [name, value] of Object.entries(raw)) {
      if (this._isReferenceOptionArray(value)) {
        // SN's `g_form.getValue` for glide_list returns the comma-joined
        // sys_id string — mirror that so client scripts see what they'd
        // see in the native portal.
        flat[name] = value.map((r) => r.sys_id).join(',');
      } else if (this._isReferenceOption(value)) {
        flat[name] = value.sys_id;
      } else {
        flat[name] = value;
      }
    }
    return flat;
  }

  private async _runOnSubmitScripts(): Promise<boolean> {
    if (!this._gForm || this._onSubmitScripts.length === 0) return true;
    // A field changed just before the click may still be waiting on its
    // GlideAjax answer (e.g. HLR Validate Requester → hlr_AjaxResult). Let it
    // land so the onSubmit check reads the real value, not a stale one.
    await this._gForm.whenIdle(SUBMIT_GLIDE_AJAX_WAIT_MS);
    this._applyGFormChanges(this._gForm.drainChanges());
    for (const script of this._onSubmitScripts) {
      const ok = await this._gForm.runScript(script, { type: 'onSubmit' });
      if (ok) continue;
      // The check may depend on a value only a GlideAjax call can provide
      // (e.g. HLR_CheckCriteriaOnSubmit reads hlr_AjaxResult). When such a
      // call could not be answered, the r2f-proc guidance is not to block
      // the form: the user already sees the "temporarily unavailable" notice.
      if (this._gForm.glideAjaxUnavailable) {
        console.info('[catalog-form] onSubmit check skipped: a GlideAjax value is unavailable');
        continue;
      }
      this.submitBlocked.set(true);
      return false;
    }
    // Apply any last-second changes the onSubmit script pushed (e.g. flipping
    // a hidden helper field). Not typically observed but keeps the mirror
    // consistent for the next open.
    this._applyGFormChanges(this._gForm.drainChanges());
    return true;
  }

  // Label + section markers are display-only — not submitted, no control.
  // Matches guide §9.2 and the initial-state rule in §5.1.
  private _isDisplayOnly(v: RenderVariable): boolean {
    return (
      v.renderType === 'label' ||
      v.renderType === 'section' ||
      v.renderType === 'section_end' ||
      v.renderType === 'split' ||
      v.renderType === 'divider'
    );
  }

  private _validatorsFor(v: RenderVariable): typeof Validators.required[] {
    if (!v.mandatory || !v.visible) return [];
    // Checkboxes semantically ask the user to opt in — `Validators.required`
    // treats `false` as valid, so a mandatory checkbox left unchecked would
    // never surface an error (a11y round 2 finding #1). Use `requiredTrue`
    // so an unchecked mandatory checkbox is flagged on touched / submit.
    return [v.renderType === 'checkbox' ? Validators.requiredTrue : Validators.required];
  }

  private _rebuildForm(vars: RenderVariable[]): void {
    const group = this._fb.group<Record<string, FormControl<unknown>>>({});
    for (const v of vars) {
      if (this._isDisplayOnly(v)) continue;
      const control = this._fb.control<unknown>(normaliseDefault(v), this._validatorsFor(v));
      if (v.readOnly) control.disable({ emitEvent: false });
      group.addControl(v.name, control);
    }
    this.form = group;
  }

  private _isReferenceVariable(v: RenderVariable): boolean {
    return (
      (v.renderType === 'reference' || v.renderType === 'reference_multi') &&
      !!v.referenceTable
    );
  }

  // Guide §5.6 — `ref_ac_columns` on the metadata attributes lists the
  // secondary columns SN would display alongside the display value
  // (semicolon-separated on the wire; Mulesoft expects comma-separated on the
  // `fields=` query param). SN emits snake_case in both item and metadata
  // responses; keep the camelCase alias as a defensive fallback for older
  // proxy shapes.
  private _extractRefColumns(v: RenderVariable): string[] {
    const raw =
      v.attributes?.['ref_ac_columns'] ?? v.attributes?.['refAcColumns'] ?? '';
    return raw
      .split(';')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  }

  private _makeReferenceFactory(v: RenderVariable): ReferenceOptionsFn {
    const table = v.referenceTable as string;
    const name = v.name;
    const isJsQualifier = v.refQualifier?.startsWith('javascript:') ?? false;
    const staticQualifier =
      v.refQualifier && !isJsQualifier ? v.refQualifier : null;
    // Guide §5.6 — `ref_ac_columns_search=true` on the metadata attributes
    // asks SN to search across the ref_ac_columns extras, not just the
    // display field. Snake_case is the real SN wire key; camelCase kept as a
    // defensive fallback. Omit the param unless explicitly set.
    const searchExtra =
      v.attributes?.['ref_ac_columns_search'] === 'true' ||
      v.attributes?.['refAcColumnsSearch'] === 'true';
    const columnsList = this._extractRefColumns(v);
    const fields = columnsList.length > 0 ? columnsList.join(',') : undefined;
    const displayField = this._displayFieldFor(v);
    if (columnsList.length > 0) {
      // Remember the column ordering so a future multi-column dropdown
      // template can render cells in the same order SN would.
      this.refColumns.update((prev) => ({ ...prev, [name]: columnsList }));
    }
    return (userQuery: string) => {
      // Empty query on a javascript: qualifier field — hand back the
      // pre-evaluated records from /evaluateRefQualifier so the dropdown
      // renders without a second round-trip (Mulesoft doc §2.4). Only skip
      // the HTTP call when we actually have prefetched rows to show.
      const prefetched = this.prefetchedRefRecords()[name];
      if (!userQuery && prefetched && prefetched.length > 0) {
        return of(prefetched);
      }
      // Prefer resolved query from POST /evaluateRefQualifier (populated once
      // on load for javascript: qualifiers); fall back to any static encoded
      // SN query the item ships with.
      // r2f 1.1.3 §6: a `javascript:` qualifier with no resolved query
      // (still pending, failed closed, or errored) offers no options — an
      // unfiltered lookup would bypass the qualifier.
      const resolved = this.resolvedRefQuery()[name];
      if (isJsQualifier && !resolved) {
        const query = userQuery.toLowerCase();
        return of((prefetched ?? []).filter((r) => r.display_value.toLowerCase().startsWith(query)));
      }
      const qualifier = (isJsQualifier ? resolved : staticQualifier) ?? undefined;
      return this._catalogFormService.referenceLookup(
        table,
        userQuery,
        REFERENCE_TYPEAHEAD_LIMIT,
        qualifier,
        searchExtra || undefined,
        fields,
        displayField,
      );
    };
  }

  // r2f 1.1.3 §11: `displayField` = `ref_ac_order_by` (default `name`).
  private _displayFieldFor(v: RenderVariable): string {
    return v.attributes?.['ref_ac_order_by'] || v.attributes?.['refAcOrderBy'] || 'name';
  }

  private _buildReferenceSearchFactories(vars: RenderVariable[]): void {
    const factories: Record<string, ReferenceOptionsFn> = {};
    for (const v of vars) {
      if (!this._isReferenceVariable(v)) continue;
      factories[v.name] = this._makeReferenceFactory(v);
    }
    this._referenceSearch = factories;
  }

  private _resolveJavascriptRefQualifiers(vars: RenderVariable[]): void {
    for (const v of vars) {
      const isRef =
        v.renderType === 'reference' || v.renderType === 'reference_multi';
      if (!isRef || !v.referenceTable) continue;
      if (!v.refQualifier?.startsWith('javascript:')) continue;
      this._catalogFormService
        .evaluateRefQualifier({
          refQualifier: v.refQualifier,
          useReferenceQualifier: v.useReferenceQualifier ?? 'advanced',
          referenceTable: v.referenceTable,
          currentValues: this._collectPlainValues(),
          search: '',
          displayField: this._displayFieldFor(v),
          limit: REFERENCE_TYPEAHEAD_LIMIT,
        })
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          next: ({ resolvedQuery, records }) => {
            this.resolvedRefQuery.update((prev) => ({
              ...prev,
              [v.name]: resolvedQuery,
            }));
            if (records.length > 0) {
              this.prefetchedRefRecords.update((prev) => ({
                ...prev,
                [v.name]: records,
              }));
            } else if (!resolvedQuery) {
              this.refUnavailable.update((prev) => ({ ...prev, [v.name]: 'empty' }));
            }
          },
          // 500 etc.: error state for this field only; no unfiltered fallback.
          error: () => this.refUnavailable.update((prev) => ({ ...prev, [v.name]: 'error' })),
        });
    }
  }
}

export type { CatalogStaticOverride } from './catalog-form.utils';
export {
  collectStaticOverrides,
  groupIntoSections,
  mergeVariables,
  normaliseDefault,
  normaliseFieldType,
  parseAttributes,
  pickLabel,
} from './catalog-form.utils';
